import { useAnalysisStore, AssessmentHistoryItem } from '../../store/analysisStore';
import { useNavigate } from 'react-router-dom';
import { computeScores } from './computeScores';
import {
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Card,
  CardActionArea,
  Stack,
  Divider,
  List,
  ListItem,
  Link as MuiLink,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeedbackIcon from '@mui/icons-material/Feedback';

export default function AssessmentHistory() {
  const {
    assessmentHistory,
    currentAssessment,
    setCurrentAssessmentFromHistory,
    removeAssessment,
    removeSourceAssessments,
    clearAssessmentHistory,
  } = useAnalysisStore();
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group assessments by source_id
  const groupedAssessments = assessmentHistory.reduce((groups, item) => {
    const key = item.source_id || 'manual-text';
    if (!groups[key]) {
      groups[key] = {
        title: item.result?.article?.title || item.url || item.input_text || 'Manual Analysis',
        url: item.url || item.input_text || 'Manual Analysis',
        items: []
      };
    }
    groups[key].items.push(item);
    return groups;
  }, {} as Record<string, { title: string; url: string; items: AssessmentHistoryItem[] }>);

  const groupKeys = Object.keys(groupedAssessments).sort((a, b) => {
    const timeA = groupedAssessments[a].items[0]?.timestamp || 0;
    const timeB = groupedAssessments[b].items[0]?.timestamp || 0;
    return timeB - timeA;
  });

  if (assessmentHistory.length === 0) {
    return (
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" mb={2}>Assessment History</Typography>
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <Typography color="text.secondary">
            No assessments yet.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Assessment History</Typography>
          <Button
            size="small"
            color="error"
            onClick={clearAssessmentHistory}
            startIcon={<DeleteIcon />}
          >
            Clear All
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {assessmentHistory.length} {assessmentHistory.length === 1 ? 'assessment' : 'assessments'}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        {groupKeys.map((sourceId) => {
          const group = groupedAssessments[sourceId];
          return (
            <Accordion 
              key={sourceId} 
              defaultExpanded={sourceId === assessmentHistory[0]?.source_id}
              sx={{ 
                mb: 1, 
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Tooltip title={group.url} placement="top" arrow>
                  <Box sx={{ width: '100%', minWidth: 0, pr: 2 }}>
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 'bold',
                      fontFamily: "'Outfit', sans-serif",
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {group.title}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                      <MuiLink 
                        component="button"
                        variant="caption" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/${sourceId}`);
                        }}
                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        View Dashboard
                      </MuiLink>
                      <Typography variant="caption" color="text.secondary">
                        • {group.items.length} records
                      </Typography>
                      {sourceId !== 'manual-text' && (
                        <IconButton
                          sx={{ minWidth: 32, minHeight: 32, p: 0.5 }}
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Permanently delete all ${group.items.length} analyses for this source?`)) {
                              removeSourceAssessments(sourceId);
                              navigate('/');
                            }
                          }}
                          title="Delete all for this source"
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1, pt: 0 }}>
                <List dense disablePadding>
                  {group.items.map((item) => {
                    const finalScore = computeScores(item.result).finalScore;
                    return (
                    <ListItem
                      key={item.id}
                      disablePadding
                      sx={{ mb: 1, position: 'relative' }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          width: '100%',
                          borderColor: currentAssessment === item.result ? 'primary.main' : 'divider',
                          bgcolor: currentAssessment === item.result ? 'primary.50' : 'inherit'
                        }}
                      >
                        <CardActionArea onClick={() => {
                          setCurrentAssessmentFromHistory(item);
                          navigate(`/assessment/${item.id}`);
                        }}>
                          <Box p={1.5}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  label={finalScore}
                                  size="small"
                                  color={item.scores.rating === 'GREEN' ? 'success' : item.scores.rating === 'YELLOW' ? 'warning' : 'error'}
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                                />
                                <Typography variant="caption" fontWeight="bold">
                                  {item.provider.toUpperCase()}
                                </Typography>
                                {item.feedback && (
                                  <Tooltip title="You left feedback on this assessment" arrow>
                                    <FeedbackIcon sx={{ fontSize: '0.95rem', color: '#1a73e8' }} />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(item.timestamp)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {item.result.article.title}
                            </Typography>
                            {item.feedback && (
                              <Typography
                                variant="caption"
                                sx={{
                                  mt: 0.5,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  color: '#1a73e8',
                                  fontStyle: 'italic',
                                }}
                              >
                                Feedback: {item.feedback}
                              </Typography>
                            )}
                          </Box>
                        </CardActionArea>
                        <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAssessment(item.id);
                            }}
                            sx={{ minWidth: 36, minHeight: 36 }}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      </Card>
                    </ListItem>
                  );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
